import React, { useCallback } from 'react';
import { EntityListResponse } from '@ydistri/api-sdk';
import { Text } from '@ydistri/ds';
import { RoutingPage } from '../../../../routes/routerSlice';
import AdministrationItemsList from '../../common/catalog/AdministrationItemsList';
import type { AdministrationItemsListItemProps } from '../../common/catalog/AdministrationItemsList';
import { EntityListTypeSupported, getEntityListSpecification } from '../entityListsLib';
import { useStableParams } from '../../../../routes/useStableParams';
import EntityListSourceTypeIcon from '../../../../components/domain/EntityLists/EntityListSourceTypeIcon';
import ArchivedEntityListIcon from '../../../../components/domain/EntityLists/ArchivedEntityListIcon';
import AdministrationItemCardPopoverContent from '../../common/AdministrationItemCardPopoverContent';

interface EntityListsListProps {
  entityListType: EntityListTypeSupported;
  data?: EntityListResponse[];
  selectedItem?: EntityListResponse;
  loading?: boolean;
}

const EntityListsList: React.FC<EntityListsListProps> = ({
  entityListType,
  data,
  loading,
  selectedItem,
}) => {
  const { projectShortName } = useStableParams();

  const getItemProps = useCallback(
    (item: EntityListResponse): AdministrationItemsListItemProps => ({
      recordId: item.entityListId,
      link: `/${projectShortName ?? ''}/${RoutingPage.PROJECT_ADMINISTRATION}/${
        getEntityListSpecification(entityListType).subpage
      }/${item.entityListId}`,
      selected: selectedItem?.entityListId === item.entityListId,
      isArchived: item.isArchived,
      cardProps: {
        name: item.name,
        description: item.description,
        updatedAt: item.lastUpdatedAt,
        updatedAtPopoverContent: <AdministrationItemCardPopoverContent item={item} />,
        countable: [
          { count: item.productCount, description: 'product' },
          { count: item.storeCount, description: 'store' },
          { count: item.skuCount, description: 'picking position' },
        ],
        summary: item.entityListImport ? <Text>- user import -</Text> : undefined,
        archiveIndicator: item.isArchived ? <ArchivedEntityListIcon /> : undefined,
        avatar: (
          <EntityListSourceTypeIcon
            entityListSourceType={item.entityListSourceTypeId}
            entityListId={item.entityListId}
            customerEntityListId={item.customerEntityListId}
            entityListVersionId={item.lastEntityListVersionId}
          />
        ),
      },
    }),
    [entityListType, projectShortName, selectedItem?.entityListId],
  );

  return (
    <AdministrationItemsList<EntityListResponse>
      data={data}
      loading={loading}
      getItemProps={getItemProps}
      data-testid="EntityListsList"
      $width="42rem"
    />
  );
};

export default EntityListsList;
